 
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ListFiltersType } from 'feature/assets/store';
import { AssetFilter, UserDefinedAssetWidgetsCard } from '@uptime-bff/api-contract';
import { GroupByTypesEnum } from 'feature/dashboard/types';
import { StepEnum, Category, GraphTypeEnum } from '../types';

export type FilterValue = string | { id: string; name: string; hasChanges?: boolean };

export interface FilterSelection {
  [category: string]: FilterValue[];
}

interface UserDefinedWidgetsState {
  id?: string;
  selectedFilters: FilterSelection;
  widgetName: string;
  activeListFilters: ListFiltersType;
  step: StepEnum;
  draftSelectedFilters: FilterSelection;
  graphType: GraphTypeEnum;
  isEditMode: boolean;
  userDefinedCardItems: UserDefinedAssetWidgetsCard[];
  backupUserDefinedCardItems: UserDefinedAssetWidgetsCard[];
  userDefinedWidgetsList?: string[];
  doneEditingUserDefinedWidgets?: boolean;
  isOpen?: boolean;
  isSorting?: boolean;
  showErrorBanner?: boolean;
  errorMessageText: string;
  showDeleteConfirmation: boolean;
  selectedCardFilters?: AssetFilter[];
  groupBy: GroupByTypesEnum;
  showUserDefinedWidgetsSection: boolean;
}

const initialState: UserDefinedWidgetsState = {
  id: '',
  selectedFilters: {},
  widgetName: '',
  selectedCardFilters: undefined,
  activeListFilters: undefined,
  step: StepEnum.Main,
  draftSelectedFilters: {},
  graphType: GraphTypeEnum.BAR,
  isEditMode: false,
  userDefinedCardItems: [],
  backupUserDefinedCardItems: [],
  isOpen: true,
  errorMessageText: '',
  doneEditingUserDefinedWidgets: false,
  showDeleteConfirmation: false,
  groupBy: GroupByTypesEnum.OPERATIONAL_STATUS,
  showUserDefinedWidgetsSection: true
};

const userDefinedWidgetsSlice = createSlice({
  name: 'userDefinedWidgets',
  initialState,
  reducers: {
    setFilter: (
      state,
      action: PayloadAction<{
        category: Category;
        values: FilterValue[];
      }>
    ) => {
      state.selectedFilters[action.payload.category] = action.payload.values;
    },
    setDraftFilter: (
      state,
      action: PayloadAction<{
        category: Category;
        values: FilterValue[];
      }>
    ) => {
      state.draftSelectedFilters[action.payload.category] = action.payload.values;
    },
    clearFilter: (state, action: PayloadAction<{ category: string; isDesktop?: boolean }>) => {
      if (action.payload.isDesktop) {
        delete state.selectedFilters?.[action.payload.category];
        delete state.draftSelectedFilters?.[action.payload.category];
      } else {
        delete state.draftSelectedFilters?.[action.payload.category];
      }
    },
    resetAllFilters: (state) => {
      state.selectedFilters = {};
      state.draftSelectedFilters = {};
      state.isEditMode = false;
      state.widgetName = '';
      state.id = '';
      state.graphType = GraphTypeEnum.BAR;
      state.step = StepEnum.Main;
      state.activeListFilters = undefined;
      state.doneEditingUserDefinedWidgets = false;
      state.selectedCardFilters = undefined;
      state.groupBy = GroupByTypesEnum.OPERATIONAL_STATUS;
    },
    setWidgetName: (state, action: PayloadAction<string>) => {
      state.widgetName = action.payload;
    },
    setActiveListFilters: (state, action: PayloadAction<UserDefinedAssetWidgetsCard>) => {
      const { assetFilter, cardName, id, graphType, groupBy } = action.payload;

      const formattedFilters = assetFilter.reduce(
        (acc: Record<string, string[]>, item: { type: string; values: string[] }) => {
          if (item?.type && Array.isArray(item.values)) {
            acc[item.type] = item.values;
          }
          return acc;
        },
        {}
      );
      state.selectedCardFilters = assetFilter;
      state.selectedFilters = formattedFilters;
      state.draftSelectedFilters = formattedFilters;
      state.widgetName = cardName;
      state.id = id;
      state.graphType = graphType === GraphTypeEnum.BAR ? GraphTypeEnum.BAR : GraphTypeEnum.LIST;
      state.groupBy = (groupBy as GroupByTypesEnum) ?? GroupByTypesEnum.OPERATIONAL_STATUS;
    },

    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEditMode = action.payload;
    },
    setIsOpenMessageModalUserDefinedWidgets: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    setUserDefinedWidgetsToBeDeleted: (state, action: PayloadAction<string[]>) => {
      state.userDefinedWidgetsList = action.payload;
    },
    setOldUserDefinedWidgets: (state, action: PayloadAction<UserDefinedAssetWidgetsCard[]>) => {
      state.backupUserDefinedCardItems = action.payload;
    },
    setUserDefinedWidgets: (state, action: PayloadAction<UserDefinedAssetWidgetsCard[]>) => {
      state.userDefinedCardItems = action.payload;
    },
    setIsSortingUserDefinedWidgets: (state, action: PayloadAction<boolean>) => {
      state.isSorting = action.payload;
    },
    setShowErrorBannerUserDefinedWidgets: (state, action: PayloadAction<boolean>) => {
      state.showErrorBanner = action.payload;
    },
    setErrorMessageUserDefinedWidgets: (state, action: PayloadAction<string>) => {
      state.errorMessageText = action.payload;
    },
    setDoneEditingUserDefinedWidgets: (state, action: PayloadAction<boolean>) => {
      state.doneEditingUserDefinedWidgets = action.payload;
    },
    setShowDeleteConfirmationUserDefinedWidgets: (state, action: PayloadAction<boolean>) => {
      state.showDeleteConfirmation = action.payload;
    },
    setWidgetStyle(state, action: PayloadAction<GraphTypeEnum>) {
      state.graphType = action.payload;
    },
    setGroupBy(state, action: PayloadAction<GroupByTypesEnum>) {
      state.groupBy = action.payload;
    },
    setShowUserDefinedWidgetsSection(state, action: PayloadAction<boolean>) {
      state.showUserDefinedWidgetsSection = action.payload;
    }
  },
});

export const {
  setFilter,
  clearFilter,
  setWidgetName,
  setDraftFilter,
  setActiveListFilters,
  resetAllFilters,
  setEditMode,
  setIsOpenMessageModalUserDefinedWidgets,
  setUserDefinedWidgetsToBeDeleted,
  setOldUserDefinedWidgets,
  setUserDefinedWidgets,
  setIsSortingUserDefinedWidgets,
  setShowErrorBannerUserDefinedWidgets,
  setErrorMessageUserDefinedWidgets,
  setDoneEditingUserDefinedWidgets,
  setShowDeleteConfirmationUserDefinedWidgets,
  setWidgetStyle,
  setGroupBy,
  setShowUserDefinedWidgetsSection
} = userDefinedWidgetsSlice.actions;
export default userDefinedWidgetsSlice.reducer;
